import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Textarea } from '@/components/elements/textarea'
import { Modal } from '@/components/Modal'
import { addTraining, updateTraining } from '@/services/trainings.service'
import { toast } from '@/utils/helpers'

const AddEditTrainingModal = ({ editTraining, resetAndClose }) => {
  const form = useForm({ defaultValues: { name: '', type: 'Business' } })
  const resetForm = () => {
    form.reset({ name: '', type: 'Business' })
    resetAndClose()
  }

  useEffect(() => {
    if (editTraining) {
      form.reset(editTraining)
    }
  }, [editTraining, form])

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateTraining } = useMutation({
    mutationFn: (data) => (editTraining ? updateTraining(data) : addTraining(data)),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} ${editTraining ? 'updated' : 'added'} successfully`)
      resetForm()

      // Refresh entities list
      queryClient.invalidateQueries({ queryKey: ['entities'] })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => mutateTraining(data)

  return (
    <Modal
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        {
          label: `${editTraining ? 'Update' : 'Add'} Training`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input id="name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea id="description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editTraining ? 'Update' : 'Add'} Training`}
      setOpen={resetForm}
    />
  )
}

AddEditTrainingModal.propTypes = {
  editTraining: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditTrainingModal
